import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import {
  CLD_CLOUD,
  CLD_PRESET_ORDER_PROOF,
  CLD_PRESET_WALLET_QR,
} from "./cloudinary";
import { validateFirebaseEnv } from "./env";

const env = process.env;

/** Show startup Firebase/Cloudinary banner. Set REACT_APP_SERVICE_STATUS=true|false */
export function isServiceStatusEnabled() {
  const raw = (env.REACT_APP_SERVICE_STATUS || "").trim().toLowerCase();
  if (raw === "true" || raw === "1") return true;
  if (raw === "false" || raw === "0") return false;
  return env.NODE_ENV === "development";
}

async function checkFirebaseAuthApi(apiKey) {
  try {
    const res = await fetch(
      `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${encodeURIComponent(apiKey)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: "bitly-env-check@example.com",
          password: "invalid-password-check",
          returnSecureToken: true,
        }),
      }
    );
    const json = await res.json();
    const msg = json?.error?.message || "";

    if (/API key not valid|API_KEY_INVALID/i.test(msg)) {
      return {
        ok: false,
        message: "Auth API key invalid — copy fresh web config from Firebase Console",
      };
    }
    if (/OPERATION_NOT_ALLOWED|CONFIGURATION_NOT_FOUND/i.test(msg)) {
      return {
        ok: false,
        message: "Enable Email/Password in Firebase Authentication",
      };
    }

    return { ok: true, message: "Auth API reachable" };
  } catch (err) {
    return { ok: false, message: err?.message || "Auth API unreachable" };
  }
}

async function checkFirestore(projectId) {
  try {
    await getDoc(doc(db, "bitly", "siteSettings"));
    return { ok: true, message: "Firestore reachable" };
  } catch (err) {
    const code = err?.code || "";
    if (code === "permission-denied") {
      return { ok: true, message: "Firestore reachable (rules restrict read)" };
    }
    if (/api-key-not-valid/i.test(err?.message || "")) {
      return { ok: false, message: "Firestore rejected API key" };
    }
    return { ok: false, message: err?.message || "Could not reach Firestore" };
  }
}

export async function checkFirebaseConnection() {
  const { ok, missing, config } = validateFirebaseEnv();
  if (!ok) {
    return {
      ok: false,
      service: "Firebase",
      message: `Missing .env: ${missing.join(", ")} — restart npm start after editing .env`,
    };
  }

  const [authCheck, firestoreCheck] = await Promise.all([
    checkFirebaseAuthApi(config.apiKey),
    checkFirestore(config.projectId),
  ]);

  if (!authCheck.ok) {
    return { ok: false, service: "Firebase", message: authCheck.message };
  }

  if (!firestoreCheck.ok) {
    return { ok: false, service: "Firebase", message: firestoreCheck.message };
  }

  return {
    ok: true,
    service: "Firebase",
    message: `Connected · ${config.projectId} · ${authCheck.message}`,
  };
}

async function pingCloudinaryPreset(preset) {
  const body = new FormData();
  body.append("upload_preset", preset);

  const res = await fetch(`https://api.cloudinary.com/v1_1/${CLD_CLOUD}/image/upload`, {
    method: "POST",
    body,
  });
  const json = await res.json();

  if (json?.error?.message) {
    const msg = json.error.message;
    if (/Missing required parameter.*file/i.test(msg)) {
      return { ok: true, message: `Preset "${preset}" ready` };
    }
    return { ok: false, message: msg };
  }

  return { ok: true, message: `Preset "${preset}" ready` };
}

export async function checkCloudinaryConnection() {
  if (!CLD_CLOUD) {
    return {
      ok: false,
      service: "Cloudinary",
      message: "REACT_APP_CLD_CLOUD is not set in .env — restart npm start",
    };
  }

  try {
    const [walletQr, orderProof] = await Promise.all([
      pingCloudinaryPreset(CLD_PRESET_WALLET_QR),
      pingCloudinaryPreset(CLD_PRESET_ORDER_PROOF),
    ]);

    if (!walletQr.ok) {
      return {
        ok: false,
        service: "Cloudinary",
        message: walletQr.message.includes("preset")
          ? walletQr.message
          : `Wallet QR preset failed: ${walletQr.message}`,
      };
    }

    if (!orderProof.ok) {
      return {
        ok: false,
        service: "Cloudinary",
        message: orderProof.message.includes("preset")
          ? orderProof.message
          : `Order proof preset failed: ${orderProof.message}`,
      };
    }

    return {
      ok: true,
      service: "Cloudinary",
      message: `Connected · ${CLD_CLOUD}`,
    };
  } catch (err) {
    return {
      ok: false,
      service: "Cloudinary",
      message: err?.message || "Network error reaching Cloudinary",
    };
  }
}

export async function runStartupHealthChecks() {
  const [firebase, cloudinary] = await Promise.all([
    checkFirebaseConnection(),
    checkCloudinaryConnection(),
  ]);

  return {
    firebase,
    cloudinary,
    allOk: firebase.ok && cloudinary.ok,
  };
}
